import {
  enumCatalogEntityType,
  searchFilterFilterType,
  searchOperator,
  type CatalogShareResourceListResponseBffApi,
  type CatalogShareResourceReadBffApi,
  type CreateCatalogShareV2MutationRequestApi,
  type EnumCatalogShareStatusApi,
  type SearchCatalogSharesBffV2MutationRequestApi,
} from '@ingenico/api-sdk-device-management-private-v2';
import { DataGridAdapter } from '@ingenico/react-ui/utils';
import type { GridSortModel } from '@mui/x-data-grid-premium';
import { toString } from 'lodash-es';
import type { CatalogShareItem, CatalogShareSearchResponse } from '../../models/catalogShare.model';
import { CATALOG_SHARE_DEFAULT_MAX_USAGE_COUNT, CatalogShareStatus } from '../../models/catalogShare.model';

const VALID_STATUSES: ReadonlyArray<EnumCatalogShareStatusApi> = Object.values(CatalogShareStatus);

function toCatalogShareStatus(raw?: string): EnumCatalogShareStatusApi {
  const upper = toString(raw).toUpperCase() as EnumCatalogShareStatusApi;
  return VALID_STATUSES.includes(upper) ? upper : CatalogShareStatus.PENDING;
}

function mapCatalogShareItem(raw: CatalogShareResourceReadBffApi): CatalogShareItem {
  return {
    id: toString(raw.id),
    version: toString(raw.target?.versionMetadata?.versionNumber),
    createdAt: raw.createdAt?.toISOString(),
    createdBy: toString(raw.createdBy?.name),
    status: toCatalogShareStatus(raw.status),
    recipients: (raw.recipients ?? []).map((r) => toString(r.email || r.normalizedEmail)),
    errorMessage: raw.statusDetail?.message ?? undefined,
  };
}

export function mapCatalogSharesResponse(raw: CatalogShareResourceListResponseBffApi): CatalogShareSearchResponse {
  return {
    data: (raw.data ?? []).map(mapCatalogShareItem),
    total: raw.pagination?.totalResults ?? 0,
  };
}

export interface MapSearchCatalogSharesRequestInput {
  readonly applicationId: string[];
  readonly searchQuery: string;
  readonly sort: GridSortModel;
}

const CATALOG_SHARE_TARGET_ID_FILTER_FIELD = 'catalogShare.targetParentId';

const CATALOG_SHARE_SORT_FIELD_BY_GRID_FIELD: Record<string, string> = {
  createdAt: 'catalogShare.createdAt',
  status: 'catalogShare.status',
  version: 'catalogShare.targetVersionNumber',
};

function mapToCatalogShareSortField(sort: GridSortModel): GridSortModel {
  return sort.map((item) => ({
    ...item,
    field: CATALOG_SHARE_SORT_FIELD_BY_GRID_FIELD[item.field] ?? item.field,
  }));
}

export function mapSearchCatalogSharesRequest({
  applicationId: applicationId,
  searchQuery,
  sort,
}: MapSearchCatalogSharesRequestInput): SearchCatalogSharesBffV2MutationRequestApi {
  const targetFilter: SearchCatalogSharesBffV2MutationRequestApi['filters'] = [
    {
      filterType: searchFilterFilterType.array,
      name: CATALOG_SHARE_TARGET_ID_FILTER_FIELD,
      operator: searchOperator.in,
      value: JSON.stringify(applicationId),
    },
  ];
  const fulltextFilter: SearchCatalogSharesBffV2MutationRequestApi['filters'] = searchQuery
    ? [
        {
          filterType: searchFilterFilterType.fulltext,
          name: '',
          operator: searchOperator.equals,
          value: `*${searchQuery}*`,
        },
      ]
    : [];
  return {
    filters: [...targetFilter, ...fulltextFilter],
    sort: DataGridAdapter.transformGridSortToRequestCall(mapToCatalogShareSortField(sort)),
  };
}

export interface MapCreateCatalogShareRequestInput {
  readonly applicationVersionId: string;
  readonly emails: string[];
}

export function mapCreateCatalogShareRequest({
  applicationVersionId,
  emails,
}: MapCreateCatalogShareRequestInput): CreateCatalogShareV2MutationRequestApi {
  return {
    maxUsageCount: CATALOG_SHARE_DEFAULT_MAX_USAGE_COUNT,
    recipients: emails.map((email) => ({ email: email.trim() })),
    target: { id: applicationVersionId.trim(), type: enumCatalogEntityType.APPLICATION_VERSION },
  };
}
